import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard, { categoryName, formatViews } from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles, hotRank, site } from "@/mocks/content";

export default function NewsDetail() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);
  const related = article
    ? (article.related
        .map((rid) => articles.find((item) => item.id === rid))
        .filter(Boolean) as typeof articles)
    : [];

  useSeo({
    title: article
      ? `${article.title} - 吃瓜日报今日瓜`
      : "瓜料未找到 - 吃瓜日报",
    description: article
      ? article.summary
      : "该瓜料可能已被合并或调整，请返回今日瓜列表查看最新爆料。",
    keywords: article
      ? `${article.tags.join(",")},网红吃瓜,今日瓜,最新爆料`
      : "网红吃瓜,今日瓜",
    canonical: article ? `https://jcspower.cn/news/${article.id}` : undefined,
    ogType: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            datePublished: article.publishedAt,
            author: { "@type": "Organization", name: site.name },
            publisher: { "@type": "Organization", name: site.name },
            keywords: article.tags.join(","),
            articleSection: categoryName(article.category),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://jcspower.cn/" },
              { "@type": "ListItem", position: 2, name: "今日瓜列表", item: "https://jcspower.cn/news" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full px-4 py-20 text-center md:px-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">这条瓜料没有找到</h1>
          <p className="mt-2 text-sm text-foreground-600">
            可能已经合并或调整，返回列表看看今天的其他瓜。
          </p>
          <Link
            to="/news"
            className="mt-5 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            返回今日瓜列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article>
        <div className="w-full border-b border-background-200/70 bg-background-100/50">
          <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
            <Breadcrumbs
              items={[
                { label: "今日瓜列表", to: "/news" },
                { label: categoryName(article.category), to: `/topic/${article.category}` },
                { label: "爆料详情" },
              ]}
            />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link
                to={`/topic/${article.category}`}
                className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                {categoryName(article.category)}
              </Link>
              {article.hot && (
                <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  <i className="ri-fire-fill text-[13px]" aria-hidden="true" />
                  热度上升中
                </span>
              )}
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-200 font-heading text-[11px] font-bold text-accent-900">
                  {article.author.slice(0, 1)}
                </span>
                {article.author}
              </span>
              <span className="h-1 w-1 rounded-full bg-background-300" />
              <span>{article.publishedAt}</span>
              <span className="h-1 w-1 rounded-full bg-background-300" />
              <span className="flex items-center gap-1">
                <i className="ri-eye-line text-sm" aria-hidden="true" />
                {formatViews(article.views)} 阅读
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-9 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="h-56 w-full overflow-hidden rounded-lg sm:h-80 md:h-96">
                <img
                  src={article.cover}
                  alt={`${article.title} 事件示意图`}
                  title={`${article.title} - 吃瓜日报今日爆料`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <p className="mt-2 text-center text-xs text-foreground-400">
                示意图 · 与本事件当事人无直接关联
              </p>

              <p className="mt-6 rounded-lg border-l-4 border-primary-400 bg-background-100/70 p-4 text-sm leading-relaxed text-foreground-700">
                {article.summary}
              </p>

              <div className="mt-6 flex flex-col gap-4">
                {article.content.map((paragraph, index) => (
                  <p key={`${article.id}-p${index}`} className="text-[15px] leading-[1.9] text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>

              <section aria-labelledby="timeline-title" className="mt-9">
                <h2 id="timeline-title" className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                    <i className="ri-time-line text-lg" aria-hidden="true" />
                  </span>
                  事件时间线
                </h2>
                <ol className="mt-4 border-l-2 border-background-200 pl-5">
                  {article.timeline.map((node) => (
                    <li key={`${article.id}-${node.time}`} className="relative pb-5 last:pb-0">
                      <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-background-50 bg-accent-500" />
                      <p className="font-heading text-sm font-semibold text-foreground-950">{node.time}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{node.text}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/news?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors duration-200 hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">围观提醒</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  本文内容均为公开信息整理，涉及争议的部分已标注为待确认。事件可能存在反转，
                  请勿对当事人进行人身攻击，也欢迎通过文末邮箱提交更正依据。
                </p>
                <Link
                  to="/about#tips"
                  className="mt-3.5 inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-foreground-800"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true" />
                  提交补充信息
                </Link>
              </div>

              {related.length > 0 && (
                <section aria-labelledby="related-title" className="mt-10">
                  <h2 id="related-title" className="font-heading text-lg font-bold text-foreground-950">
                    相关瓜料推荐
                  </h2>
                  <div className="mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    {related.map((item) => (
                      <ArticleCard key={item.id} article={item} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="flex flex-col gap-6 lg:col-span-4">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-500 text-background-50">
                    <i className="ri-fire-fill text-xs" aria-hidden="true" />
                  </span>
                  吃瓜热榜
                </h2>
                <ol className="mt-4 flex flex-col">
                  {hotRank.map((item, index) => (
                    <li key={item.id} className="border-b border-background-200/70 last:border-0">
                      <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-600">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-primary-500 p-5 text-background-50">
                <h2 className="font-heading text-base font-bold">手上有新瓜？</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-100/90">
                  匿名提交公开可查的线索，我们会在核实后跟进整理。
                </p>
                <Link
                  to="/about#tips"
                  className="mt-4 inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-600 transition-colors duration-200 hover:bg-background-100"
                >
                  去投稿
                  <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}